import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { UserService } from 'app/services/user.service';
import { environment } from 'environments/environment';
import { AnnouncementService } from 'app/services/Announcement/announcement.service';
import { NotificationService } from 'app/services/notification.service';
import { CreateAnnouncementView } from 'app/models/announcements/CreateAnnouncementView';
import { NavigationService } from 'app/services/NavigationService.service';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import {
	AuthenticationResult,
	InteractionStatus,
	PopupRequest,
	RedirectRequest,
	EventMessage,
	EventType,
	EndSessionRequest,
} from '@azure/msal-browser';
import { LayoutService } from 'app/services/layout.service';
import { User } from 'app/models/User';
import { PageTitleService } from 'app/services/page-title.service';
import { AvailableFormsService } from 'app/services/available-forms.service';
import { ClientsService } from 'app/services/clients.service';
import { SignalRHubService } from 'app/services/signalr-hub.service';
import { ClientSettingsPanelService } from 'app/services/client-settings-panel.service';
import { ChatAlertService } from 'app/services/form/chat-alert.service';
import { ChatAlert } from 'app/models/forms/vendor/chat-alert.model';

@Component({
	selector: 'navbar-cmp',
	templateUrl: 'navbar.component.html',
	standalone: false,
})
export class NavbarComponent implements OnInit {
	private clientId: string;
	private userId: string;
	user: User;
	canToggleNewLayout: boolean = false;
	location: Location;
	public open: boolean = false;
	environmentName: string = environment.name;
	isProduction: boolean = environment.production;
	announcements: CreateAnnouncementView[];
	unreadAnnouncements: any;
	chatAlerts: ChatAlert[] = [];
	unseenChatMessages = 0;
	isNewLayout: boolean = false;
	/** Gates the Client Settings gear (EN-3503): SYS_ADMIN or Aclarian Support. */
	canManageClientSettings: boolean;

	@ViewChild('navbar-cmp', { static: false }) button;

	constructor(
		location: Location,
		private userService: UserService,
		private notifyService: NotificationService,
		private announcementService: AnnouncementService,
		private router: Router,
		private navigation: NavigationService,
		private authService: MsalService,
		public layoutService: LayoutService,
		public pageTitleService: PageTitleService,
		private readonly availableFormsService: AvailableFormsService,
		private readonly clientService: ClientsService,
		private signalRHubService: SignalRHubService,
		private clientSettingsPanelService: ClientSettingsPanelService,
		private chatAlertService: ChatAlertService
	) {
		this.clientId = this.userService.currentUserValue.user.clientId;
		this.userId = this.userService.currentUserValue.user.id;
		this.canManageClientSettings = this.userService.isAdmin();
		this.location = location;
		this.user = this.userService.currentUserValue.user;

		layoutService.isNewLayout$.subscribe((isNew) => {
			this.isNewLayout = isNew;
		});
	}

	/** True when the gear should be shown: an admin with a client currently selected. */
	get showClientSettingsGear(): boolean {
		return this.canManageClientSettings && !!this.clientId && this.clientId !== 'undefined';
	}

	/** Opens the Client Settings slide-out panel for the currently selected client (EN-3503). */
	openClientSettings(): void {
		this.clientSettingsPanelService.open(this.clientId);
	}

	ngOnInit() {
		this.canToggleNewLayout =
			this.user?.email === 'jromager@aclarian.com' || this.user?.email === 'sarcila@aclarian.com';

		if (this.clientId && this.clientId != 'undefined') {
			this.initializeSignalR();
		}
	}

	private initializeSignalR() {
		this.signalRHubService.initialize(this.userId, this.clientId);
		this.signalRHubService.on('announcement', () => {
			this.getAnnouncement();
		});
		this.signalRHubService.on('announcementStatusChanged', () => {
			this.getAnnouncement();
		});
		this.signalRHubService.on('chatAlertReceived', () => {
			this.getChatAlerts();
		});
		this.signalRHubService.whenConnected().subscribe(() => {
			this.getAnnouncement();
			this.getChatAlerts();
			this.signalRHubService.send('JoinRegistrationInquiries').catch(() => {});
		});
	}

	getChatAlerts() {
		this.chatAlertService.getAlerts(this.clientId).subscribe({
			next: (alerts) => {
				this.chatAlerts = alerts ?? [];
				this.unseenChatMessages = this.chatAlertService.countUnseen(this.chatAlerts);
			},
			error: () => {
				this.chatAlerts = [];
				this.unseenChatMessages = 0;
			},
		});
	}

	get unreadNotificationCount(): number {
		return Number(this.unreadAnnouncements ?? 0) + this.unseenChatMessages;
	}

	/** Opening the bell clears its badge: announcements are marked read, chat alerts dismissed. */
	onNotificationsOpened() {
		this.chatAlertService.markSeen(this.chatAlerts);
		this.unseenChatMessages = 0;
		this.markAnnouncementsRead();
	}

	private markAnnouncementsRead() {
		const unread = (this.announcements ?? []).filter((announcement) => !announcement.announcementFors[0].isRead);
		if (unread.length === 0) return;

		unread.forEach((announcement) =>
			this.announcementService
				.announcementMarkAsRead(this.clientId, announcement.announcementFors[0].id, this.userId)
				.subscribe({ error: () => {} })
		);
		localStorage.setItem('unreadAnnouncements', '0');
		this.unreadAnnouncements = '0';
	}

	getAnnouncement() {
		this.announcementService.getAnnouncementByUserId(this.clientId, this.userId, 1, 10).subscribe(
			(response) => {
				this.announcements = response.filter((x) => !x.announcementFors[0].isRead);
				localStorage.setItem('unreadAnnouncements', response.filter((x) => !x.announcementFors[0].isRead).length);
				this.getUnreadAnnouncementCount();
			},
			() => {
				this.notifyService.showError('Unable to load announcements.', 'Error');
			}
		);
	}

	getUnreadAnnouncementCount() {
		this.unreadAnnouncements = localStorage.getItem('unreadAnnouncements');
	}

	isMobileMenu() {
		return window.outerWidth >= 991;
	}

	getPath() {
		return this.location.prepareExternalUrl(this.location.path());
	}

	logout() {
		var isSSOLogin = this.userService.isSSOLogin();
		var msalAccount = this.userService.getMsalAccount();
		this.signalRHubService.stop();
		this.userService.logout();
		this.availableFormsService.clearOnLogout();
		this.clientService.clearClientFormsConfig();
		if (isSSOLogin) {
			const logoutRequest: EndSessionRequest = {
				account: msalAccount,
				postLogoutRedirectUri: environment.baseUrl,
			};
			this.authService.logout(logoutRequest);
		}
		this.router.navigateByUrl('/authentication/login').then();
	}

	showBackBtn(): boolean {
		return this.navigation.hasHistory();
	}

	back() {
		this.navigation.back();
	}

	closeDropdown(event: Event) {
		const target = (event.target as HTMLElement).closest('.dropdown');
		if (target) {
			const menu = target.querySelector('.dropdown-menu');
			if (menu && menu.classList.contains('show')) {
				menu.classList.remove('show');
			}
		}
	}
}
